import React from 'react';
import { sumBy } from 'lodash';
import { formatNumber } from '../../../lib/format';
import { useDefaultCurrency } from '../../../state/zustand';
import type { ClientId } from '../../core/ClientId';
import type { FuelEuCost } from '../../pnl/co2/co2Cost';
import {
  useCo2ProcessorErrors,
  useGetFuelEuCostsByShipmentId,
} from '../../pnl/ui/context';
import type { CostParam } from '../costs/costs';
import { createPnlSubRows, SummaryRow } from './SummaryRow';

export function FuelEuCosts({
  externalValuesVisible,
  isVisible,
  shipmentIds,
}: {
  externalValuesVisible: boolean;
  isVisible: boolean;
  shipmentIds: Set<ClientId>;
}) {
  if (!isVisible) return null;
  return Array.from(shipmentIds).map((shipmentId) => (
    <ShipmentFuelEuCost
      key={shipmentId}
      shipmentId={shipmentId}
      externalValuesVisible={externalValuesVisible}
    />
  ));
}

function ShipmentFuelEuCost({
  externalValuesVisible,
  shipmentId,
}: {
  externalValuesVisible: boolean;
  shipmentId: ClientId;
}) {
  const getFuelEuCostsByShipment = useGetFuelEuCostsByShipmentId();
  const fuelEuCosts = getFuelEuCostsByShipment(shipmentId).filter(
    (cost) => cost.countryFactor > 0,
  );
  const defaultCurrency = useDefaultCurrency();

  if (fuelEuCosts.length === 0) return null;

  const total = sumBy(fuelEuCosts, (cost) => cost.amount);

  const subRows = createPnlSubRows(fuelEuCosts, {
    baseCurrency: defaultCurrency,
    keyFn: ({ locations, portCallId }) =>
      `${locations.map((loc) => loc.name).join(' - ')}-${portCallId ?? ''}`,
    labelFn: (costs) =>
      `${costs[0].locations.map((loc) => loc.name).join(' - ')}`,
    showExternalValues: externalValuesVisible,
  });

  return (
    <SummaryRow
      currency={defaultCurrency}
      name="FuelEU penalty/overcompensation"
      subRows={subRows}
      value={total}
      isHiddenIfNull
      externalValue={externalValuesVisible ? null : undefined}
    />
  );
}

export function FuelEuBalance({
  externalValuesVisible,
  isVisible,
  shipmentIds,
}: {
  externalValuesVisible: boolean;
  isVisible: boolean;
  shipmentIds: Set<ClientId>;
}) {
  if (!isVisible) return null;
  return Array.from(shipmentIds).map((shipmentId) => (
    <ShipmentFuelEuBalance
      key={shipmentId}
      shipmentId={shipmentId}
      externalValuesVisible={externalValuesVisible}
    />
  ));
}

function ShipmentFuelEuBalance({
  externalValuesVisible,
  shipmentId,
}: {
  externalValuesVisible: boolean;
  shipmentId: ClientId;
}) {
  const getFuelEuCostsByShipment = useGetFuelEuCostsByShipmentId();
  const processorErrors = useCo2ProcessorErrors();
  const errorsMessages = processorErrors.map((err) => err.error);

  const allFuelEuCosts = getFuelEuCostsByShipment(shipmentId);
  if (allFuelEuCosts.length === 0) return null;

  const shownFuelEuCosts = allFuelEuCosts.filter(
    (cost) => cost.countryFactor > 0,
  );

  if (shownFuelEuCosts.length === 0) {
    return (
      <SummaryRow
        currency={null}
        name="FuelEU Compliance balance"
        subRows={[]}
        value={0}
        valueFormatFn={() => ''}
        params={[
          {
            name: 'Fuel EU not applicable',
            value: 'Outside Fuel EU area',
            source: 'SEABER',
          },
        ]}
        externalValue={externalValuesVisible ? null : undefined}
        errors={errorsMessages}
      />
    );
  }

  const subRows = createPnlSubRows(shownFuelEuCosts, {
    keyFn: ({ locations, portCallId }) =>
      `${locations.map((loc) => loc.name).join(' - ')}-${portCallId ?? ''}`,
    labelFn: (costs) =>
      `${costs[0].locations.map((loc) => loc.name).join(' - ')}`,
    showExternalValues: externalValuesVisible,
    valueFn: (cost) => cost.complianceBalance,
    valueFormatFn: (value) =>
      `${formatNumber(value, 2, undefined, undefined)} gCO2eq/MJ`,
    paramFn: (costs) => [
      {
        name: 'GHG Intensity (gCO2eq/MJ)',
        value: costs[0].ghgIntensity,
        source: 'SEABER',
      },
      ...getFuelEuCostParams(costs),
    ],
  });

  return (
    <SummaryRow
      currency={null}
      name="FuelEU Compliance balance"
      params={[
        {
          name: `GHG Intensity target for ${shownFuelEuCosts[0].year} (gCO2eq/MJ)`,
          value: shownFuelEuCosts[0].ghgIntensityTarget,
          source: 'SEABER',
        },
        ...getFuelEuCostParams(shownFuelEuCosts),
      ]}
      subRows={subRows}
      value={0}
      valueFormatFn={() => ''}
      externalValue={externalValuesVisible ? null : undefined}
    />
  );
}

function getFuelEuCostParams(fuelEuCosts: FuelEuCost[]): CostParam[] {
  const isSummaryRow = fuelEuCosts.length === 1;

  const totalTtwEmissions = sumBy(
    fuelEuCosts,
    (cost) => cost.totalTankToWakeEmissions,
  );

  const totalWellToTankEmissions = sumBy(
    fuelEuCosts,
    (cost) => cost.totalWellToTankEmissions,
  );

  const totalEnergyMJ = sumBy(fuelEuCosts, (cost) => cost.totalEnergyMJ);

  return [
    {
      name: `Total tank-to-wake emissions (tCO2eq)`,
      value: formatNumber(totalTtwEmissions / 1000, 0),
      source: 'SEABER',
    },
    {
      name: `Total well-to-tank emissions  (tCO2eq)`,
      value: formatNumber(totalWellToTankEmissions / 1000, 0),
      source: 'SEABER',
    },
    {
      name: `Total energy used (MJ)`,
      value: formatNumber(totalEnergyMJ, 0),
      source: 'SEABER',
    },
    ...(isSummaryRow
      ? [
          {
            name: 'Intra/Extra-EU factor',
            value: fuelEuCosts[0].countryFactor,
            source: 'SEABER',
          } as const,
        ]
      : []),
  ];
}

// NOTE: Left for reference, not used in the UI for the while being
// function calculateOverallFuelEuComplianceBalance(costs: FuelEuCost[]): number {
//   // Weight by effective energy (totalEnergyMJ × countryFactor)
//   const totalEffectiveEnergyMJ = sumBy(
//     costs,
//     (c) => c.totalEnergyMJ * c.countryFactor,
//   );

//   if (totalEffectiveEnergyMJ <= 0) return 0;

//   const weightedSum = sumBy(
//     costs,
//     (c) => c.complianceBalance * (c.totalEnergyMJ * c.countryFactor),
//   );

//   return weightedSum / totalEffectiveEnergyMJ;
// }
